/**
 * The list order shared by leagues, tournaments and drop-ins (PR 6); mirrors
 * backend/src/utils/listOrder.js. Live first, then open, then upcoming
 * (soonest first), then completed (newest first), then cancelled; id DESC last.
 */

const STATUS_RANK = {
  in_progress: 1,
  active: 1,
  registration_open: 2,
  open: 2,
  registration_closed: 3,
  draft: 3,
  completed: 4,
  cancelled: 5,
};
const OTHER_RANK = 3;
const UPCOMING_MAX_RANK = 3;

export const listRank = (status) => STATUS_RANK[String(status || '').toLowerCase()] ?? OTHER_RANK;

const timeOf = (value) => {
  const t = value == null || value === '' ? NaN : new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Comparator for Array.prototype.sort.
 * @param {Function} pick  item → { status, start, end, id }
 */
export const compareListOrder = (pick) => (a, b) => {
  const x = pick(a);
  const y = pick(b);
  const rx = listRank(x.status);
  const ry = listRank(y.status);
  if (rx !== ry) return rx - ry;
  const upcoming = rx <= UPCOMING_MAX_RANK;
  const tx = upcoming ? timeOf(x.start) : timeOf(x.end) ?? timeOf(x.start);
  const ty = upcoming ? timeOf(y.start) : timeOf(y.end) ?? timeOf(y.start);
  if (tx !== ty) {
    if (tx == null) return 1;
    if (ty == null) return -1;
    return upcoming ? tx - ty : ty - tx;
  }
  return Number(y.id) - Number(x.id);
};

/** A drop-in's sort fields (camelCase from the API model, snake_case tolerated). */
const dropInFields = (d) => {
  const at = d.scheduledAt || d.scheduled_at;
  return { status: d.status, start: at, end: at, id: d.id };
};

/** Hosted and joined drop-ins as one list: each once, in list order. */
export const mergeMyDropIns = (hosted = [], joined = []) => {
  const seen = new Set();
  return [...hosted, ...joined]
    .filter((d) => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    })
    .sort(compareListOrder(dropInFields));
};

/**
 * The viewer's own items (joined, entered or hosted) first, everything else
 * after, each part keeping its order. `mine` is a Set (or array) of ids.
 */
export const mineFirst = (items = [], mine = new Set()) => {
  const ids = mine instanceof Set ? mine : new Set(mine);
  if (!ids.size) return items;
  const own = [];
  const rest = [];
  for (const item of items) (ids.has(item?.id) ? own : rest).push(item);
  return [...own, ...rest];
};
